import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Minus } from "lucide-react";
import { Modal } from "../../UI/Modal";
import Button from "../../UI/Button";

export interface MetricEditConfig {
  metricKey: "tonnage" | "distanceKm" | "hours";
  title: string;
  unit: string;
  icon: React.ReactNode;
  iconClassName: string;
  planned?: number;
  actual?: number;
  /** System-suggested auto value + an explanatory note (e.g. "3 rounds × 25t"). */
  autoValue?: number;
  autoNote?: string;
  step: number;
  quickAdjust: number[];
}

interface MetricEditSheetProps {
  config: MetricEditConfig | null;
  onClose: () => void;
  onSave: (value: number | undefined, note?: string) => void;
}

const round1 = (v: number) => Math.round(v * 10) / 10;

/** Tap-to-edit numeric metric editor — big stepper + quick-adjust + optional note. */
export const MetricEditSheet: React.FC<MetricEditSheetProps> = ({ config, onClose, onSave }) => {
  const { t } = useTranslation("works");
  const [value, setValue] = useState<number>(0);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (config) {
      setValue(config.actual ?? config.autoValue ?? config.planned ?? 0);
      setNote("");
    }
  }, [config]);

  if (!config) return null;

  const adjust = (delta: number) => setValue((v) => Math.max(0, round1(v + delta)));

  return (
    <Modal isOpen={!!config} onClose={onClose} title={config.title} size="sm">
      <div className="flex-1 overflow-y-auto px-4 py-4 sm:p-6 space-y-4">
        <div className="flex flex-col items-center text-center gap-1.5">
          <span className={`h-12 w-12 rounded-xl flex items-center justify-center ${config.iconClassName}`}>
            {config.icon}
          </span>
          {config.planned !== undefined && (
            <div className="text-xs text-[#8A9590]">
              {t("driver.metricEdit.planned")}: {config.planned} {config.unit}
            </div>
          )}
        </div>

        {/* Big stepper */}
        <div className="flex items-stretch rounded-xl border-2 border-[#C97A3C] overflow-hidden">
          <button
            type="button"
            onClick={() => adjust(-config.step)}
            className="w-16 bg-[#FCEEE2] text-[#C97A3C] flex items-center justify-center hover:bg-[#F8E0CC] transition-colors"
            aria-label={t("driver.metricEdit.decrease")}
          >
            <Minus size={22} />
          </button>
          <div className="flex-1 flex items-center justify-center py-4">
            <input
              type="number"
              min={0}
              step={config.step}
              value={Number.isInteger(value) ? value : round1(value)}
              onChange={(e) => setValue(Math.max(0, Number(e.target.value) || 0))}
              className="w-full text-center text-3xl font-bold text-[#0B0F0E] bg-transparent focus:outline-none"
            />
          </div>
          <div className="px-4 flex items-center bg-[#FAF9F6] border-l border-[#ECEAE4] text-[#8A9590] font-semibold">
            {config.unit}
          </div>
        </div>

        {config.autoValue !== undefined && config.autoNote && (
          <div className="bg-[#E8F3EC] rounded-lg px-3 py-2 text-xs text-[#2F7A4E] flex items-center justify-between gap-2">
            <span>
              <strong>{t("driver.metricEdit.autoLabel")}:</strong> {config.autoNote}
            </span>
            <button
              type="button"
              onClick={() => setValue(config.autoValue!)}
              className="font-semibold underline whitespace-nowrap"
            >
              {t("driver.metricEdit.useAuto")}
            </button>
          </div>
        )}

        {config.quickAdjust.length > 0 && (
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[#8A9590] mb-1.5">
              {t("driver.metricEdit.quickAdjust")}
            </div>
            <div className="flex flex-wrap gap-2">
              {config.quickAdjust.map((delta) => (
                <button
                  key={delta}
                  type="button"
                  onClick={() => adjust(delta)}
                  className="px-3 py-1.5 rounded-lg border border-[#ECEAE4] bg-[#FAF9F6] text-sm text-[#2B2F2E] hover:border-[#C97A3C] transition-colors"
                >
                  {delta > 0 ? `+${delta}` : delta} {config.unit}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-[#8A9590] mb-1.5">
            {t("driver.metricEdit.note")}
          </label>
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t("driver.metricEdit.notePlaceholder")}
            className="w-full px-3 py-2 text-sm rounded-lg border border-[rgba(11,15,14,0.15)] bg-white text-[#2B2F2E] placeholder:text-[#8A9590] focus:outline-none focus:ring-2 focus:ring-[#C97A3C]/30 focus:border-[#C97A3C] resize-none"
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 px-4 py-3 sm:px-6 border-t border-[rgba(11,15,14,0.08)] bg-white shrink-0">
        <Button variant="outline" onClick={onClose}>
          {t("form.cancel")}
        </Button>
        <Button variant="primary" onClick={() => onSave(value, note.trim() || undefined)}>
          {t("driver.metricEdit.save", { value: Number.isInteger(value) ? value : round1(value), unit: config.unit })}
        </Button>
      </div>
    </Modal>
  );
};
